import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import type { SortingFn } from "@tanstack/react-table";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date/datetime value using the user's local timezone.
 * Accepts Date objects, ISO strings, or undefined.
 * Returns fallback (default "—") when value is falsy.
 */
export function formatDateTime(
  value: Date | string | undefined | null,
  fmt = "dd MMM yyyy, HH:mm",
  fallback = "—",
): string {
  if (!value) return fallback;
  const date = typeof value === "string" ? new Date(value) : value;
  if (isNaN(date.getTime())) return fallback;
  return format(date, fmt);
}

/**
 * Format a date (date-only, no time) value using the user's local timezone.
 */
export function formatDate(
  value: Date | string | undefined | null,
  fmt = "dd MMM yyyy",
  fallback = "—",
): string {
  return formatDateTime(value, fmt, fallback);
}

/**
 * TanStack Table sorting function for date/datetime columns.
 * Handles both Date objects and ISO date strings coming from the server.
 * Usage: `sortingFn: dateSortFn` on any column definition.
 */
export const dateSortFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);
  const aTime = a instanceof Date ? a.getTime() : a ? new Date(a as string).getTime() : 0;
  const bTime = b instanceof Date ? b.getTime() : b ? new Date(b as string).getTime() : 0;
  return aTime - bTime;
};

export function formatCurrency(
  value: number | string | undefined | null,
  currency = "AED",
  locale = "en-AE",
): string {
  const num = typeof value === "string" ? Number(value) : value;
  if (num === undefined || num === null || Number.isNaN(num)) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(num);
}
